import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { ConfirmationComponent } from '../../../shared/confirmation/confirmation.component';
import { DisplayLabel } from '../../../shared/models/displayLabel';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { RSJService } from '../../../shared/services/rsj.service';
import { StructureService } from '../../../shared/services/structure.service';
import { UserService } from '../../../shared/services/user.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { DateValidators } from '../../../shared/validators/date.validators';
import { InputValidators } from '../../../shared/validators/input.validators';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { ReferentDto } from '../../dto/referent.dto';
import { RsjDto } from '../dto/rsj.dto';

@Component({
  selector: 'app-rsj-personal-data',
  templateUrl: './rsj-personal-data.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class RsjPersonalDataComponent implements OnInit, OnDestroy {
  protected readonly destroy$: Subject<void> = new Subject();

  public beneficiary: BeneficiaryDto;
  public rsjForm: UntypedFormGroup;
  private user: UserDto = undefined;
  public rsjDto: RsjDto = undefined;
  public beneficiaryReferents: ReferentDto[] = [];
  public referentList: UserDto[] = [];
  public rsjStates: DisplayLabel[] = [];
  public rsjCloseReasons: DisplayLabel[] = [];
  public submitted: boolean = false;
  public isStructure: boolean = false;
  public hasOrientation: boolean = true;
  public isInvalidReferent: boolean = false;
  public isntBeginWithinOri: boolean = false;
  public isntEndWithinOri: boolean = false;
  public hasHistorizedRsjReferent: boolean = false;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly authService: AuthService,
    private readonly rsjService: RSJService,
    private readonly toastService: ToastService,
    private readonly userService: UserService,
    private readonly structureService: StructureService,
    private readonly modalService: NgbModal,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();

    if (this.user.groups.includes('STRUCTURE')) {
      this.isStructure = true;
    }

    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    if (!this.beneficiary.rsjId) {
      await this.rsjService.saveRSJ({ beneficiaryId: this.beneficiary.id, rsjState: 7 } as RsjDto);
      this.beneficiariesService.findById(this.beneficiary.id);
    }

    this.hasOrientation = !!this.beneficiary.orientation;

    this.rsjService.rsj$.pipe(takeUntil(this.destroy$)).subscribe((rsj: RsjDto) => (this.rsjDto = rsj));
    if (!this.rsjDto) {
      await this.rsjService.getRSJ(this.beneficiary.id);
    }

    await this.initLists();

    this.rsjForm = this.fb.group(
      {
        rsjState: [
          {
            value: this.rsjDto?.rsjState ? this.rsjDto.rsjState : 7,
            disabled: this.isStructure,
          },
          [Validators.required],
        ],
        beginDateRsj: [
          {
            value: this.rsjDto?.beginDateRsj,
            disabled: this.isStructure,
          },
          [DateValidators.dateFormatValidator],
        ],
        endDateRsj: [
          {
            value: this.rsjDto?.endDateRsj,
            disabled: this.isStructure,
          },
          [DateValidators.dateFormatValidator],
        ],
        rsjCloseReason: [
          {
            value: this.rsjDto?.rsjCloseReason,
            disabled: this.isStructure,
          },
        ],
        referentForm: this.fb.group(
          {
            id: [this.rsjDto?.currentReferent?.id],
            beginDateReferent: [this.rsjDto?.currentReferent?.beginDate, [DateValidators.dateFormatValidator]],
            endDateReferent: [this.rsjDto?.currentReferent?.endDate, [DateValidators.dateFormatValidator]],
            referent: [this.rsjDto?.currentReferent?.user.id],
          },
          {
            validators: [
              DateValidators.datesOrderValidator('beginDateReferent', 'endDateReferent'),
              InputValidators.rsjReferentValidator('referent', 'beginDateReferent', 'endDateReferent'),
            ],
          },
        ),
      },
      {
        validators: [DateValidators.datesOrderValidator('beginDateRsj', 'endDateRsj')],
      },
    );

    this.sortHistorized();

    this.rsjForm.controls.rsjState.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((rsjState: number) => this.handleStateChange(rsjState));

    this.referentForm.controls.referent.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((referent: number) => {
      if (!referent && this.rsjDto) {
        this.referentForm.controls.id.setValue(undefined);
      }
    });

    this.referentForm.controls.beginDateReferent.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((beginDate: Date) => {
      if (beginDate && moment(beginDate).isValid()) {
        this.isntBeginWithinOri = this.verifOrientation(beginDate);
      } else {
        this.isntBeginWithinOri = false;
      }
    });

    this.referentForm.controls.endDateReferent.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((endDate: Date) => {
      if (endDate && moment(endDate).isValid()) {
        this.isntEndWithinOri = this.verifOrientation(endDate);
      } else {
        this.isntEndWithinOri = false;
      }
    });
  }

  public sortHistorized(): void {
    this.beneficiaryReferents = [...(this.beneficiary.referents ?? [])].sort((a: ReferentDto, b: ReferentDto) =>
      a.beginDate < b.beginDate ? 1 : -1,
    );
    this.hasHistorizedRsjReferent = this.beneficiaryReferents.filter((referent) => referent.type === 'RSJ' && referent.historized).length > 0;
  }

  public verifOrientation(date: Date): boolean {
    if (this.beneficiary.orientation.endDate) {
      return !moment(date).isBetween(this.beneficiary.orientation.beginDate, this.beneficiary.orientation.endDate, undefined, '[]');
    } else {
      return moment(date).isBefore(this.beneficiary.orientation.beginDate);
    }
  }

  private handleStateChange(rsjState: number): void {
    if ([2, 3, 4, 5].includes(rsjState)) {
      this.rsjForm.controls.beginDateRsj.addValidators(Validators.required);
      if (rsjState === 5) {
        this.rsjForm.controls.endDateRsj.addValidators(Validators.required);
        this.rsjForm.controls.rsjCloseReason.addValidators(Validators.required);
      } else {
        this.rsjForm.controls.endDateRsj.setValidators(DateValidators.dateFormatValidator);
        this.rsjForm.controls.rsjCloseReason.setValidators([]);
      }
    } else {
      this.rsjForm.controls.beginDateRsj.setValidators(DateValidators.dateFormatValidator);
      this.rsjForm.controls.endDateRsj.setValidators(DateValidators.dateFormatValidator);
      this.rsjForm.controls.rsjCloseReason.setValidators([]);
    }
    this.rsjForm.controls.beginDateRsj.updateValueAndValidity();
    this.rsjForm.controls.endDateRsj.updateValueAndValidity();
    this.rsjForm.controls.rsjCloseReason.updateValueAndValidity();
  }

  public async onSubmit() {
    this.submitted = true;
    this.referentHistoryValidation();

    try {
      if (this.referentForm.valid && !this.isInvalidReferent) {
        let beneficiaryToSave: BeneficiaryDto = JSON.parse(JSON.stringify(this.beneficiary));
        beneficiaryToSave.referents = this.beneficiaryReferents;
        if (
          !this.referentForm.controls.id.value &&
          this.beneficiaryReferents.findIndex((referent) => referent.type === 'RSJ' && !referent.historized) >= 0
        ) {
          this.beneficiaryReferents.splice(
            this.beneficiaryReferents.findIndex((referent) => referent.type === 'RSJ' && !referent.historized),
            1,
          );
        } else if (this.referentForm.controls.id.value) {
          let referent = this.beneficiaryReferents.find((referent) => referent.id === this.referentForm.controls.id.value);
          referent.user = { id: this.referentForm.controls.referent.value, structureId: this.beneficiary.orientation.structure.id } as UserDto;
          referent.beginDate = this.referentForm.controls.beginDateReferent.value;
          referent.endDate = this.referentForm.controls.endDateReferent.value;
        } else if (this.referentForm.controls.referent.value) {
          let currentReferent: ReferentDto = {
            id: this.referentForm.controls.id.value,
            user: { id: this.referentForm.controls.referent.value, structureId: this.beneficiary.orientation.structure.id } as UserDto,
            beginDate: this.referentForm.controls.beginDateReferent.value,
            endDate: this.referentForm.controls.endDateReferent.value,
            type: 'RSJ',
            beneficiaryId: this.beneficiary.id,
            historized: false,
          } as ReferentDto;
          beneficiaryToSave.referents = this.beneficiaryReferents.concat(currentReferent);
        }
        // to keep because json.stringify of beneficiary stringify dates
        beneficiaryToSave.orientation.beginDate = this.beneficiary.orientation.beginDate;
        beneficiaryToSave.orientation.endDate = this.beneficiary.orientation.endDate;
        this.beneficiariesService.updateBeneficiary(beneficiaryToSave);
      }
    } catch (error) {
      this.toastService.show('La sauvegarde a échouée, veuillez vérifier les données.', 'danger');
      return;
    }

    if (this.rsjForm.valid && !this.isntBeginWithinOri && !this.isntEndWithinOri) {
      const saveDto = new RsjDto();
      saveDto.id = this.rsjDto?.id;
      saveDto.beneficiaryId = this.beneficiary.id;
      saveDto.rsjState = this.rsjForm.controls.rsjState.value;
      saveDto.beginDateRsj = this.rsjForm.controls.beginDateRsj.value;
      saveDto.endDateRsj = this.rsjForm.controls.endDateRsj.value;
      saveDto.rsjCloseReason = this.rsjForm.controls.rsjCloseReason.value;
      await this.rsjService.saveRSJ(saveDto);
      this.toastService.show('Le parcours RSJ du bénéficiaire a été sauvegardé avec succès.', 'success');
    } else {
      this.toastService.show('La sauvegarde a échouée, veuillez vérifier les données.', 'danger');
    }
  }

  public cancel(): void {
    this.rsjForm.controls.rsjState.setValue(this.rsjDto ? this.rsjDto.rsjState : undefined);
    this.rsjForm.controls.beginDateRsj.setValue(this.rsjDto ? this.rsjDto.beginDateRsj : undefined);
    this.rsjForm.controls.endDateRsj.setValue(this.rsjDto ? this.rsjDto.endDateRsj : undefined);
    this.rsjForm.controls.rsjCloseReason.setValue(this.rsjDto ? this.rsjDto.rsjCloseReason : undefined);

    this.referentForm.controls.id.setValue(this.rsjDto?.currentReferent ? this.rsjDto.currentReferent.id : undefined);
    this.referentForm.controls.beginDateReferent.setValue(this.rsjDto?.currentReferent ? this.rsjDto.currentReferent.beginDate : undefined);
    this.referentForm.controls.endDateReferent.setValue(this.rsjDto?.currentReferent ? this.rsjDto.currentReferent.endDate : undefined);
    this.referentForm.controls.referent.setValue(this.rsjDto?.currentReferent ? this.rsjDto.currentReferent.user.id : undefined);
    this.beneficiaryReferents = this.beneficiary.referents ?? [];
    this.sortHistorized();
    this.referentForm.updateValueAndValidity();
    this.rsjForm.updateValueAndValidity();
  }

  public async initLists(): Promise<void> {
    [this.rsjStates, this.rsjCloseReasons, this.referentList] = await Promise.all([
      this.rsjService.findRsjStates(),
      this.rsjService.findRsjReasons(),
      this.beneficiary.orientation.structure.id ? this.userService.findAll(this.beneficiary.orientation.structure.id) : [],
    ]);
  }

  public async historizeReferent(): Promise<void> {
    this.setReferentValidator();
    if (this.referentForm.valid) {
      await this.addNewReferentHistory();
      this.hasHistorizedRsjReferent = true;
    }
  }

  private setReferentValidator(): void {
    this.referentForm.controls.endDateReferent.addValidators(Validators.required);
    this.referentForm.controls.beginDateReferent.addValidators(Validators.required);
    this.referentForm.controls.referent.addValidators(Validators.required);
    this.referentForm.controls.endDateReferent.updateValueAndValidity();
    this.referentForm.controls.beginDateReferent.updateValueAndValidity();
    this.referentForm.controls.referent.updateValueAndValidity();
  }

  private resetReferent(onlyValidator: boolean = false): void {
    if (!onlyValidator) {
      this.referentForm.controls.beginDateReferent.reset();
      this.referentForm.controls.endDateReferent.reset();
      this.referentForm.controls.referent.reset();
    }
    this.referentForm.controls.beginDateReferent.removeValidators(Validators.required);
    this.referentForm.controls.referent.removeValidators(Validators.required);
    this.referentForm.controls.endDateReferent.removeValidators(Validators.required);
    this.referentForm.controls.endDateReferent.updateValueAndValidity();
    this.referentForm.controls.beginDateReferent.updateValueAndValidity();
    this.referentForm.controls.referent.updateValueAndValidity();
  }

  private async addNewReferentHistory(): Promise<void> {
    if (!this.isntBeginWithinOri && !this.isntEndWithinOri) {
      const user: UserDto = await this.userService.findUser(this.referentForm.controls.referent.value);
      if (this.referentForm.controls.id.value) {
        let referent = this.beneficiaryReferents.find((referent) => referent.id === this.referentForm.controls.id.value);
        referent.user = user;
        referent.beginDate = this.referentForm.controls.beginDateReferent.value;
        referent.endDate = this.referentForm.controls.endDateReferent.value;
        referent.historized = true;
      } else {
        let newHistorizedReferent: ReferentDto = {
          id: this.referentForm.controls.id.value,
          user: user,
          beginDate: this.referentForm.controls.beginDateReferent.value,
          endDate: this.referentForm.controls.endDateReferent.value,
          beneficiaryId: this.beneficiary.id,
          type: 'RSJ',
          historized: true,
        } as ReferentDto;
        if (this.beneficiaryReferents) {
          this.beneficiaryReferents.push(newHistorizedReferent);
        } else {
          this.beneficiaryReferents = [newHistorizedReferent];
        }
      }

      this.beneficiaryReferents.sort((a: ReferentDto, b: ReferentDto) => (a.beginDate < b.beginDate ? 1 : -1));
      this.resetReferent();
    }
  }

  public async deleteReferentHistory(referentHistory: ReferentDto): Promise<void> {
    if (this.user?.groups && ['ADMIN', 'DIRECTION+'].some((profil) => this.user.groups.includes(profil))) {
      const confirm: boolean = await this.openConfirmationModal();
      if (confirm) {
        this.beneficiaryReferents.splice(
          this.beneficiaryReferents.findIndex((item: ReferentDto) => {
            return item.id === referentHistory.id;
          }),
          1,
        );
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation de supprimer une historisation.", 'danger');
    }
  }

  private referentHistoryValidation(): void {
    this.isInvalidReferent = false;
    const rsjHistorizedReferent: ReferentDto[] = this.beneficiaryReferents.filter((referent) => referent.type === 'RSJ' && referent.endDate);
    for (let i: number = 0; i < rsjHistorizedReferent.length; i++) {
      if (
        !rsjHistorizedReferent[i].beginDate ||
        !rsjHistorizedReferent[i].endDate ||
        moment(rsjHistorizedReferent[i].beginDate).isSameOrAfter(rsjHistorizedReferent[i].endDate) ||
        (i + 1 < rsjHistorizedReferent.length && moment(rsjHistorizedReferent[i].beginDate).isSameOrBefore(rsjHistorizedReferent[i + 1].endDate))
      ) {
        this.isInvalidReferent = true;
      }
    }
    if (rsjHistorizedReferent[0] && this.referentForm.controls.referent.value) {
      if (moment(this.referentForm.controls.beginDateReferent.value).isSameOrBefore(rsjHistorizedReferent[0].endDate)) {
        this.isInvalidReferent = true;
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer cette historisation de référent ?');
  }

  public get referentForm(): UntypedFormGroup {
    return this.rsjForm.controls.referentForm as UntypedFormGroup;
  }
}
